import {IconButton, openConfirmDialog, Tooltip} from 'platform/components';
import {HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {always, isNotNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  AdvertisementStatusEnum,
  type CommonDescriptionAttribute,
  useGetGeneralSettingsQuery,
  useGetVehicleQuery,
  useRepeatLastActionMutation,
  useUpdateVehicleOnPlatformMutation,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum, EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  convertHtmlToFormat,
  countMobileDeMarkupOverhead,
  DEFAULT_PLATFORM_FORMAT,
  handleApiError,
  PLATFORM_FORMAT_MAP,
  usePermissions,
  usePkwEnvkvAdvertisingPreflight,
} from '@dms/shared';

import {useAdvertisementPolling} from '../contexts/AdvertisementPollingContext';
import {useOpenPlatformDetail} from '../hooks/useOpenPlatformDetail';

interface ActionsProps {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
  currentStatus: AdvertisementStatusEnum;
  platformTitle: string;
  inSale: boolean;
  statusTriggeredAt: string | null;
  canPreview?: boolean;
  onPreview?: (platformCode?: AdvertisingPlatformCodeEnum) => void;
}

export const PlatformActions = (props: ActionsProps) => {
  const [updateVehicleOnPlatform] = useUpdateVehicleOnPlatformMutation();
  const [repeatLastAction] = useRepeatLastActionMutation();
  const {runPkwEnvkvPreflight, isChecking} = usePkwEnvkvAdvertisingPreflight();
  const {resetPollingInterval} = useAdvertisementPolling();
  const [clickedAt, setClickedAt] = useState<string | null | undefined>(undefined);
  const {data: generalSettings} = useGetGeneralSettingsQuery({vehicleId: props.vehicleId});

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });

  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});

  const [
    hasEditPlatformSettingsPermission,
    hasRepeatLastPublishingActionPermission,
    hasUpdateVehiclePlatformPermission,
  ] = usePermissions({
    permissionKeys: ['editPlatformSettings', 'repeatLastPublishingAction', 'updateVehiclePlatform'],
    scopes: {
      editPlatformSettings: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      repeatLastPublishingAction: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      updateVehiclePlatform: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  // Prevent BE errors when interval between multiple triggers is less than approx. 5s
  const preventMultipleTriggers = clickedAt === props.statusTriggeredAt;

  const executeUpdate = () => {
    resetPollingInterval();
    setClickedAt(props.statusTriggeredAt);
    updateVehicleOnPlatform({vehicleId: props.vehicleId, platformCode: props.platformCode})
      .unwrap()
      .catch(handleApiError);
  };

  const runLimitCheckAndUpdate = () => {
    const descriptionAttribute = generalSettings?.general?.attributes?.find(
      (attr): attr is CommonDescriptionAttribute => attr.code === 'description'
    );
    const descriptionHtml = descriptionAttribute?.value ?? null;

    if (isNotNil(descriptionHtml)) {
      const config = PLATFORM_FORMAT_MAP[props.platformCode] ?? DEFAULT_PLATFORM_FORMAT;
      const {characterLimit} = config;

      if (isNotNil(characterLimit)) {
        const converted = convertHtmlToFormat(descriptionHtml, config.format);
        const isMobileDe = config.format === 'mobileDe';
        const markupOverhead = isMobileDe ? countMobileDeMarkupOverhead(converted) : 0;
        const count = isMobileDe ? converted.length - markupOverhead : converted.length;

        if (count > characterLimit) {
          openConfirmDialog({
            text: `${i18n.t('page.advertisement.labels.characterLimitExceeded')}\n\n${props.platformTitle}: ${count} / ${characterLimit} (${i18n.t('page.advertisement.labels.exceededBy')} ${count - characterLimit})`,
            // The confirmation can stay open for a while: re-check the vehicle first.
            onConfirm: () => runPkwEnvkvPreflight(props.vehicleId, executeUpdate),
          });
          return;
        }
      }
    }

    executeUpdate();
  };

  const handleUpdate = () => {
    void runPkwEnvkvPreflight(props.vehicleId, runLimitCheckAndUpdate);
  };

  const executeRepeatLastAction = () => {
    resetPollingInterval();
    setClickedAt(props.statusTriggeredAt);
    repeatLastAction({vehicleId: props.vehicleId, platformCode: props.platformCode})
      .unwrap()
      .catch(handleApiError);
  };

  const handleRepeatLastAction = () => {
    // Repeating a failed unpublish never goes through the EnVKV preflight; the
    // backend stays the authority for what the repeat actually does.
    if (props.currentStatus === AdvertisementStatusEnum.UNPUBLISH_FAILED) {
      executeRepeatLastAction();
      return;
    }

    void runPkwEnvkvPreflight(props.vehicleId, executeRepeatLastAction);
  };

  const openPlatformDetail = useOpenPlatformDetail({
    vehicleId: props.vehicleId,
    isDisabled: !hasEditPlatformSettingsPermission,
  });
  const handleOpenDetail = () => openPlatformDetail(props.platformCode, props.platformTitle);

  const showUpdateAction = props.currentStatus === AdvertisementStatusEnum.PUBLISHED;
  const isFastback = props.platformCode === AdvertisingPlatformCodeEnum.FASTBACK_B2B;

  const showRepeatAction = match(props.currentStatus)
    .with(
      AdvertisementStatusEnum.PUBLISH_FAILED,
      AdvertisementStatusEnum.UNPUBLISH_FAILED,
      AdvertisementStatusEnum.UPDATE_FAILED,
      always(true)
    )
    .otherwise(always(false));
  const showFastbackResend =
    isFastback && props.currentStatus === AdvertisementStatusEnum.PUBLISHED;

  return (
    <HStack align="center" justify="flex-end">
      <Show when={props.canPreview}>
        <Tooltip label={i18n.t('general.actions.preview')}>
          <IconButton
            icon="action/visibility"
            onClick={() => props.onPreview?.(props.platformCode)}
            data-testid={`${props.platformCode}-preview-ad`}
          />
        </Tooltip>
      </Show>

      <Show when={hasRepeatLastPublishingActionPermission && showRepeatAction && props.inSale}>
        <Tooltip label={i18n.t('page.advertisement.actions.repeatLastAction')}>
          <IconButton
            icon="AV/replay"
            onClick={handleRepeatLastAction}
            data-testid={`${props.platformCode}-repeat-last-action`}
            isDisabled={preventMultipleTriggers || isChecking}
          />
        </Tooltip>
      </Show>

      <Show when={hasRepeatLastPublishingActionPermission && showFastbackResend && props.inSale}>
        <Tooltip label={i18n.t('page.advertisement.actions.repeatLastAction')}>
          <IconButton
            icon="AV/replay"
            onClick={handleRepeatLastAction}
            data-testid={`${props.platformCode}-resend`}
            isDisabled={preventMultipleTriggers}
          />
        </Tooltip>
      </Show>

      <Show
        when={hasUpdateVehiclePlatformPermission && showUpdateAction && props.inSale && !isFastback}
      >
        <Tooltip label={i18n.t('page.advertisement.actions.updateAd')}>
          <IconButton
            icon="action/update"
            onClick={handleUpdate}
            data-testid={`${props.platformCode}-update-ad`}
            isDisabled={preventMultipleTriggers || isChecking}
          />
        </Tooltip>
      </Show>

      <Tooltip
        label={`${i18n.t('page.advertisement.actions.editPlatform')} ${props.platformTitle}`}
      >
        <IconButton
          icon="navigation/chevron_right"
          onClick={handleOpenDetail}
          data-testid={`link-advertisement-platforms.${props.platformCode}`}
        />
      </Tooltip>
    </HStack>
  );
};
